import type {
  SystemAttributeToReviewFragment,
  WorkerAttributeToReviewFragment,
  WorkerMandatoryAttributesQuery,
} from "@sinch/components/application/ProfileReviewDialog/queries.types";
import { AttributeTypeEnum } from "@sinch/core/entities/serverEnums";
import { ensureArray } from "ramda-adjunct";

type MandatoryAttribute = WorkerMandatoryAttributesQuery["workerMandatoryAttribute"][number];

export const isAttribute = (attribute: MandatoryAttribute): attribute is WorkerAttributeToReviewFragment =>
  attribute.__typename === "AttributeValue";

export const isSystem = (attribute: MandatoryAttribute): attribute is SystemAttributeToReviewFragment =>
  attribute.__typename === "WorkerSystemAttribute";

type EditableFile = { id: string; name: string; url: string };

/**
 * Prepare a media value for the inline edit input of the pending changes table.
 *
 * The row starts from the worker's raw `pendingValue`, which for media types is the list of file
 * hashes, while the upload input expects `{id, name, url}` items and spreads each one. A bare hash
 * would come back as `{"0": "a", "1": "f", ...}` without an `id` and reach the backend as garbage,
 * so swap each stored hash for its pending file. Entries from an earlier edit already are files.
 */
export const toEditableFileValue = (
  dataType: AttributeTypeEnum | null | undefined,
  value: unknown,
  pendingFiles: EditableFile[] | null | undefined
): unknown => {
  const isMediaType =
    dataType === AttributeTypeEnum.Gallery || dataType === AttributeTypeEnum.Image || dataType === AttributeTypeEnum.File;
  if (!isMediaType || value === null || value === undefined) {
    return value;
  }

  return ensureArray(value).map((entry) =>
    typeof entry === "string" ? (pendingFiles?.find((file) => file.id === entry) ?? { id: entry, name: entry, url: "" }) : entry
  );
};
